import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import NewPage from "../../../layouts/page/NewPage";
import Form from "./Form";
import MenuConfig from "../../../../constants/MenuConfig";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";
import { successAlert } from "../../../components/alert/defaultAlert";
import axiosInstance from "../../../../services/AxiosInstance";
import { convertStringToStringDouble } from "../../../../utils/ConverterUtils";
import { useBranch } from "../../../../context/BranchContext";
import { currentMonth, monthToApi } from "./bonusMonth";

const EmployeeBonusNew = () => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const [resetForm, setResetForm] = useState(false);
    const [entityData, setEntityData] = useState(null);
    const navigate = useNavigate();

    // O mês inicial é criado uma vez e congelado: com enableReinitialize, um new Date() nos
    // initialValues reinicializaria o form a cada render e apagaria o que está sendo digitado
    // (o mesmo cuidado do Vale).
    const [startMonth] = useState(currentMonth);

    const getInitialValues = () => ({
        employees: [],
        value: '',
        startMonth,
        endMonth: null,
        description: '',
        branch: selectedBranch ? { value: selectedBranch.id, label: selectedBranch.name } : null
    });

    const formSchema = Yup.object().shape({
        employees: Yup.array().min(1, t('cash_flow.employee_bonus_validation_employees')),
        value: Yup.number()
            .typeError(t('validation.required.value'))
            .positive(t('validation.required.value'))
            .required(t('validation.required.value')),
        startMonth: Yup.date().required(t('cash_flow.employee_bonus_validation_start_month')).nullable(),
        endMonth: Yup.date().nullable()
            .test('end-after-start', t('cash_flow.employee_bonus_validation_end_month'), function (value) {
                const { startMonth: start } = this.parent;
                return !value || !start || monthToApi(value) >= monthToApi(start);
            }),
        branch: Yup.object().required(t('validation.required.branch')).nullable(),
        description: Yup.string().max(255, t('validation.max_length', { max: 255 }))
    });

    const formik = useFormik({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        validationSchema: formSchema,
        onSubmit: (values) => {
            // O mês vai como texto "AAAA-MM-01" no fuso de quem lançou, pelo mesmo motivo do
            // Vale: o Date cru sai em UTC e pode virar o mês anterior no servidor.
            const payload = {
                value: convertStringToStringDouble(values.value),
                startMonth: monthToApi(values.startMonth),
                endMonth: monthToApi(values.endMonth),
                description: values.description
            };

            // Na edição só valor, vigência e motivo mudam; no cadastro vai a lista de quem
            // recebe, e o servidor cria uma bonificação para cada um.
            const request = entityData
                ? axiosInstance.patch(`${Api.cash_flow.employee_bonus}/${entityData.id}`, payload)
                : axiosInstance.post(`${Api.cash_flow.employee_bonus}/batch`, {
                    ...payload,
                    branch: { id: values.branch.value },
                    employeeIds: values.employees.map(employee => employee.value)
                });

            request
                .then(() => {
                    successAlert(t('message.operation_successfully'), () => {
                        if (entityData == null) {
                            onReset();
                        } else {
                            navigate(MenuConfig.cash_flow.employee_bonus.route);
                        }
                    });
                })
                .catch(error => ApiError(error))
                .finally(() => formik.setSubmitting(false));
        }
    });

    const onReset = () => {
        // Mantém a filial do contexto para o lançamento seguinte, como no Vale.
        formik.resetForm({ values: getInitialValues() });
        setResetForm((current) => !current);
    }

    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    }

    return (
        <>
            <NewPage
                motherMenu={t('cash_flow.label')}
                childMenu={t('cash_flow.employee_bonuses')}
                activeMenu={entityData ? t('update') : t('new')}
                child={
                    <Form
                        formik={formik}
                        entityData={entityData}
                        resetForm={resetForm}
                    />
                }
                setEntityData={setEntityData}
                path={Api.cash_flow.employee_bonus}
                isSubmitting={formik.isSubmitting}
                handleSubmit={onSubmit}
                cancelRoute={MenuConfig.cash_flow.employee_bonus.route}
            />
        </>
    );
}

export default EmployeeBonusNew;
